import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import type { z } from 'zod'
import { AuthShell } from '../../components/shell/AuthShell'
import { Field } from '../../components/ui/Field'
import { FormCard } from '../../components/ui/FormCard'
import { loginSchema } from '../../schemas/adminSchemas'
import { api } from '../../lib/api'
import { setAuth } from '../../store/authSlice'

export function LoginPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [apiError, setApiError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    setApiError('')
    try {
      const { data } = await api.post('/admin/auth/login', values)
      dispatch(setAuth({ token: data.token, admin: data.admin }))
      navigate('/dashboard')
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
      setApiError(message || 'Login failed')
    }
  }

  return (
    <AuthShell title="Admin Sign In" subtitle="Enter your credentials to continue">
      <FormCard
        title="Login"
        submitLabel={form.formState.isSubmitting ? 'Signing in…' : 'Sign In'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="grid gap-4">
          <Field label="Email" error={form.formState.errors.email?.message} {...form.register('email')} />
          <label className="block text-sm font-semibold text-leaf-dark">
            Password
            <div className="mt-2 flex rounded-lg border border-stone-200 bg-white focus-within:border-mango-orange focus-within:ring-4 focus-within:ring-mango-yellow/25">
              <input
                type={showPassword ? 'text' : 'password'}
                className="min-w-0 flex-1 rounded-l-lg px-3 py-2.5 text-sm text-stone-900 outline-none"
                {...form.register('password')}
              />
              <button type="button" className="grid w-11 place-items-center text-stone-500 hover:text-leaf-dark" onClick={() => setShowPassword(v => !v)}>
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {form.formState.errors.password && <p className="mt-1 text-xs font-medium text-red-600">{form.formState.errors.password.message}</p>}
          </label>
          {apiError && <p className="text-sm text-red-600">{apiError}</p>}
        </div>
      </FormCard>
    </AuthShell>
  )
}
